import { Injectable, Injector } from "@angular/core";
import { Headers, RequestOptions, Response } from "@angular/http";
import { Cart } from "../_models/index";
import { AuthenticationService } from "./authentication.service";
import { Subject } from "rxjs/Subject";
import { of } from "rxjs/observable/of";
import { HttpWrapperService } from "./http.wrapper.service";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable } from "rxjs/Observable";
import { AlertService } from "./alert.service";
import { Router } from "@angular/router";
import { I18nService } from "./i18n.service";
import { SessionManagerService } from "./SessionManager/session-manager.service";
import {
  ServerCustomCode,
  serverCustomCodeHeaderName,
} from "../_models/serverCustomCode";
import { eventsDisplayBug } from "../../bugs/eventsDisplayBug";

@Injectable()
export class DataService {
  static readonly MINUTES_DISCONNECTED_MESSAGE_THRESHOLD = 60;
  cart = new Subject<Cart>();
  children = new Subject<any>();
  registeredUser = new Subject<any>();
  events = new Subject<any>();
  texts = new Subject<any>();
  messages = new Subject<any>();
  isRedirectedToLogin: boolean = false;

  constructor(
    private authenticationService: AuthenticationService,
    private i18nService: I18nService,
    private http: HttpWrapperService,
    private injector: Injector,
    private router: Router,
    private sessionManager: SessionManagerService
  ) {}

  /** This function is for targeting responses without
   * a body. the components cannot handle these errors */

  public httpCatch = (err: HttpErrorResponse) => {
    if (err && err.status === 401 && !this.isRedirectedToLogin) {
      this.isRedirectedToLogin = true;
      const message = "נותקת מהמערכת עקב חוסר פעילות. התחבר מחדש.";
      const alertService = this.injector.get(AlertService);

      if (
        this.sessionManager.getMinuetsFromLastLogin() <
        DataService.MINUTES_DISCONNECTED_MESSAGE_THRESHOLD
      ) {
        alertService.error(message, true);
      }
      setTimeout(() => this.router.navigate(["/login"]), 1000);
      return new Observable(() => () => true);
    }
    throw err;
  };

  getMessages() {
    this.setTranslationsInMessages();
    return this.http.get("/api/global").map((response: Response) => {
      if (response.json().nests) {
        localStorage.setItem("nests", JSON.stringify(response.json().nests));
      }
      if (response.json().allNests) {
        localStorage.setItem(
          "allNests",
          JSON.stringify(response.json().allNests)
        );
      }
      if (response.json().layers) {
        localStorage.setItem("layers", JSON.stringify(response.json().layers));
      }
      if (response.json().docs) {
        localStorage.setItem("docs", JSON.stringify(response.json().docs));
      }
      if (response.json().sitecptch) {
        localStorage.setItem(
          "sitecptch",
          JSON.stringify(response.json().sitecptch)
        );
      }
      if (response.json().generalTexts) {
        localStorage.setItem(
          "generalTexts",
          JSON.stringify(response.json().generalTexts)
        );
      }
      if (response.json().countries) {
        localStorage.setItem(
          "countries",
          JSON.stringify(response.json().countries)
        );
      }
      return response.json();
    });
  }

  getEventsFromData(data) {
    if (data.contributions) {
      localStorage.setItem("contributions", JSON.stringify(data.contributions));
    } else if (data.items && data.items.contributions) {
      localStorage.setItem(
        "contributions",
        JSON.stringify(data.items.contributions)
      );
    }
    return data && data.events
      ? data.events
      : data.items && data.items.events
      ? data.items.events
      : [];
  }

  getUsersData(childId) {
    const url =
      childId && childId !== null
        ? "/services/add-child?child=" + childId
        : "/services/events";
    return this.http
      .get(url)
      .map((response: Response) => {
        this.handleEventsResponse(response, url);
      })
      .catch(this.httpCatch);
  }

  removeChildFromList(childId) {
    const url = "/services/remove-child?child=" + childId;
    return this.http.get(url).map((response: Response) => {
      let cart =
        response.json() && response.json().cart ? response.json().cart : {};
      this.cart.next(cart);
      localStorage.setItem("cart", JSON.stringify(cart));
      let data = this.getData(response, url);

      this.events.next(this.getEventsFromData(data));
      if (response.json().children) {
        this.children.next(response.json().children);
      }
    });
  }

  handleEventsResponse(response: Response, url: string) {
    let data = this.getData(response, url);

    let ev = this.getEventsFromData(data);
    let ch = response.json().children;
    let texts = response.json().texts;
    this.children.next(ch);
    if (ch && ch.length > 0) {
      this.authenticationService.setSiteId(ch[0].site);
      if (texts) {
        localStorage.setItem("texts", JSON.stringify(texts));
      } else {
        this.getTexts(ch[0].childId).subscribe();
      }
      let currentUser = JSON.parse(localStorage.getItem("currentUser"));
      currentUser.videoId = ch[0].videoId;
      localStorage.setItem("currentUser", JSON.stringify(currentUser));
    }
    this.events.next(ev);
  }

  updateNotification() {
    return this.http
      .post("/services/update-notification", "", this.headers())
      .map((response: Response) => {
        return response.json();
      });
  }

  postEntity(url: string, entity: any, api?: boolean) {
    const urlForApi = api ? "/api/" : "/services/";
    return this.http
      .post(urlForApi + url, entity)
      .map((response: Response) => {
        return response.json();
      })
      .catch(this.httpCatch);
  }

  getExcel(url: string, entity: any): Observable<null | ServerCustomCode> {
    // tslint:disable-next-line:ban-types
    const httpOption: Object = {
      observe: "response",
      headers: new Headers({
        "Content-Type": "application/json; charset=utf-8",
      }),
      responseType: 3,
    };
    return this.http
      .post("/services/" + url, entity, httpOption)
      .map((response: Response) => {
        if (response.headers.has(serverCustomCodeHeaderName)) {
          return +response.headers.get(
            serverCustomCodeHeaderName
          ) as ServerCustomCode;
        }

        const fileName = "results.xlsx";
        //@ts-expect-error - response body type
        if (window.navigator.msSaveOrOpenBlob) {
          //@ts-expect-error - this should work
          window.navigator.msSaveBlob(response["_body"], fileName);
        } else {
          const link = window.URL.createObjectURL(
            new Blob([response["_body"]], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            })
          );
          const a = document.createElement("a");
          document.body.appendChild(a);
          a.setAttribute("style", "display: none");
          a.href = link;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(link);
          a.remove();
        }
        return null;
      });
  }

  checkout(payment) {
    return this.http
      .post("/services/checkout", payment, this.headers())
      .map((response: Response) => {
        if (this.checkResponse(response)) {
          return response.json();
        } else {
          throw new Error();
        }
      });
  }

  checkoutMeshutaf() {
    return this.http
      .post("/services/checkout-meshutaf", null, this.headers())
      .map((response: Response) => {
        if (this.checkResponse(response)) {
          return response.json();
        } else {
          throw new Error();
        }
      });
  }

  addChildMeshutaf(postData) {
    return this.http
      .post("/services/add-child-meshutaf", postData, this.headers())
      .map((response: Response) => {
        if (this.checkResponse(response)) {
          //
        } else {
          throw new Error();
        }

        if (response.json().events) {
          this.events.next(response.json().events);
        }
        return response.json();
      });
  }

  okPayment(okPaymentData) {
    return this.http
      .post("/api/okpage", okPaymentData, this.headers())
      .map((response: Response) => {
        this.cart.next(null);
        localStorage.setItem("cart", JSON.stringify({}));
        return response.json();
      });
  }

  newsletter(newsEmailData) {
    return this.http
      .post("/api/newsletter", newsEmailData, this.headers())
      .map((response: Response) => {
        return response.json();
      });
  }

  forgotPassword(emailData) {
    return this.http
      .post("/api/forgot-password", emailData, this.headers())
      .map((response: Response) => {
        return response.json();
      });
  }

  changePassword(uid, changePasswordData) {
    return this.http
      .post("/api/change-password/" + uid, changePasswordData, this.headers())
      .map((response: Response) => {
        return response.json();
      });
  }

  getForgotPasswordData(uid) {
    return this.http
      .get("/api/forgot-password-data/" + uid)
      .map((response: Response) => response.json());
  }

  getContentData() {
    return this.http
      .get("/services/content")
      .map((response: Response) => response.json());
  }

  getCircleData() {
    return this.http
      .get("/services/circle-data")
      .map((response: Response) => response.json());
  }

  saveCircleData(circleData) {
    return this.http
      .post("/services/save-circle-data", circleData, this.headers())
      .map((response: Response) => {
        if (this.checkResponse(response)) {
          return response.json();
        } else {
          throw new Error();
        }
      });
  }

  saveContentCircle(contentData) {
    return this.http
      .post("/services/save-content-circle", contentData, this.headers())
      .map((response: Response) => {
        if (this.checkResponse(response)) {
          return response.json();
        } else {
          throw new Error();
        }
      });
  }

  getRefundsData() {
    return this.http
      .get("/services/circle-refunds")
      .map((response: Response) => response.json());
  }

  saveRefundData(refundData) {
    return this.http
      .post("/services/save-refund-circle", refundData, this.headers())
      .map((response: Response) => {
        return response.json();
      });
  }

  deleteRefundData(refundData) {
    return this.http
      .post("/services/delete-refund-circle", refundData, this.headers())
      .map((response: Response) => {
        if (this.checkResponse(response)) {
          return response.json();
        } else {
          throw new Error();
        }
      });
  }

  getAdminEvents(type: number, text: string, childId?: number) {
    return this.http
      .post(
        "/services/admin-events?type=" +
          type +
          (childId ? "&childId=" + childId : ""),
        { text: text },
        this.headers()
      )
      .map((response: Response) => response.json());
  }

  getAdminUsers(text) {
    return this.http
      .post("/services/meshutaf-users", { text: text }, this.headers())
      .map((response: Response) => {
        return response && response.json() ? response.json().users : [];
      });
  }

  saveAdminUser(user) {
    return this.http
      .post("/services/save-user", user, this.headers())
      .map((response: Response) => response.json());
  }

  getPaymentFilters() {
    return this.http
      .get("/services/payment_filters")
      .map((response: Response) => {
        if (this.checkResponse(response)) {
          if (response.json().cashierStatuses) {
            localStorage.setItem(
              "cashierStatuses",
              JSON.stringify(response.json().cashierStatuses)
            );
          }
          return response.json();
        } else {
          throw new Error();
        }
      });
  }

  getMihlolEvents(eventId: any, childId?: any) {
    return this.http
      .get(
        "/services/mihlol-events?eventId=" +
          eventId +
          (childId ? "&childId=" + childId : "")
      )
      .map((response: Response) => response.json());
  }

  saveMihlolEvents(mihlol: any, childId?: number) {
    const url =
      "/services/save-mihlol" + (childId ? "?childId=" + childId : "");
    return this.http
      .post(url, mihlol, this.headers())
      .map((response: Response) => {
        this.handleEventsResponse(response, url);
        return response.json();
      });
  }

  contactForm(contactFormData) {
    return this.http
      .post("/api/contact-form", contactFormData, this.headers())
      .map((response: Response) => {
        return response.json();
      });
  }

  refund(paymentItem, check, cancelCash) {
    return this.http
      .post(
        "/services/refund" +
          (check ? "?check=true" : "") +
          (cancelCash ? "?cancelCash=true" : ""),
        paymentItem,
        this.headers()
      )
      .map((response: Response) => {
        return response.json();
      });
  }

  refundRequest(data) {
    return this.http
      .post("/services/refund-request", data, this.headers())
      .map((response: Response) => {
        return response.json();
      });
  }

  sendPaymentError(error) {
    return this.http
      .post("/api/payment-error", error, this.headers())
      .map((response: Response) => {
        return {};
      });
  }

  getTexts(childId) {
    return this.http
      .get("/services/texts?child=" + childId)
      .map((response: Response) => {
        if (response && response.json()) {
          localStorage.setItem("texts", JSON.stringify(response.json()));
        }
      });
  }

  addToCart(item) {
    return this.http
      .post("/services/add-to-cart", item, this.headers())
      .map((response: Response) => {
        let cart =
          response && response.json() && response.json().cart
            ? response.json().cart
            : {};
        this.cart.next(cart);
        localStorage.setItem("cart", JSON.stringify(cart));
        let items =
          response.json() && response.json().items ? response.json().items : {};
        this.events.next(this.getEventsFromData(items));
        return cart;
      });
  }

  updateCart(item) {
    return this.http
      .post("/services/update-cart", item, this.headers())
      .map((response: Response) => {
        let cart =
          response.json() && response.json().cart ? response.json().cart : {};
        this.cart.next(cart);
        localStorage.setItem("cart", JSON.stringify(cart));
        return cart;
      });
  }

  public removeFromCart(item) {
    return this.http
      .post("/services/remove-from-cart", item, this.headers())
      .map((response: Response) => {
        let cart =
          response.json() && response.json().cart ? response.json().cart : {};
        this.cart.next(cart);
        localStorage.setItem("cart", JSON.stringify(cart));
        let items =
          response.json() && response.json().items ? response.json().items : {};
        this.events.next(this.getEventsFromData(items));
        return cart;
      });
  }

  clearCart() {
    return this.http.get("/services/clear-cart").map((response: Response) => {
      return;
    });
  }

  registerUser(user) {
    return this.http
      .post("/api/register-user", user, this.headers())
      .map((response: Response) => response.json());
  }

  saveChild(child, doNotHandleAdditionalData) {
    let url = "/services/save-child";
    return this.http
      .post(url, child, this.headers())
      .map((response: Response) => {
        if (doNotHandleAdditionalData) {
          response.json();
        } else {
          this.handleEventsResponse(response, url);
        }
        return response.json();
        // 	        	let data = this.getData(response,url);
        // 	        	let events = this.getEventsFromData(data);
        // 	            this.events.next(events);
        // 	            this.children.next(response.json().children);
        // 			return events;
      });
  }

  getHistory(nestId?: number, userId?: number) {
    return this.http
      .get(
        "/services/history" +
          (nestId ? "?nest=" + nestId : "") +
          (userId ? "?user=" + userId : "")
      )
      .map((response: Response) => response.json());
  }

  getBrothers() {
    return this.http
      .get("/services/brothers")
      .map((response: Response) => {
        return response.json();
      })
      .catch(this.httpCatch);
  }

  saveBrothers(brothers) {
    return this.http
      .post("/services/savebrothers", brothers, this.headers())
      .map((response: Response) => response.json());
  }

  sendFiles(files, paymentReturnId, childId) {
    return this.http
      .post("/services/upload/" + paymentReturnId + "/" + childId, files)
      .map((response: Response) => response.json());
  }

  approveEventMeshutaf(postData) {
    return this.http
      .post("/services/approve-event-meshutaf", postData, this.headers())
      .map((response: Response) => response.json());
  }

  search(text: string) {
    if (text === "") {
      return of([]);
    }
    return this.http
      .post("/services/search", { text: text })
      .map((response: Response) => response.json().entities);
  }

  searchChild(text: string) {
    if (text === "") {
      return of([]);
    }
    return this.http
      .post("/services/children", { text: text })
      .map((response: Response) => response.json().children);
  }

  searchParent(text: string) {
    if (text === "") {
      return of([]);
    }
    return this.http
      .post("/services/parents", { text: text })
      .map((response: Response) => response.json().parents);
  }

  postEntityAutocomplete(url: string, postEntity: any, dataName?: string) {
    return this.http
      .post("/services/" + url, postEntity)
      .map((response: Response) => {
        return response.json() && dataName
          ? response.json()[dataName]
          : response.json();
      });
  }

  postEntityMultiSelect(url: string, postEntity: any, dataName?: string) {
    return this.http
      .post("/services/" + url, postEntity)
      .map((response: Response) => {
        return response.json() && dataName
          ? response.json()[dataName]
          : response.json();
      });
  }

  checkResponse(response) {
    try {
      let r = response && response.json() ? response.json() : null;
      return true;
    } catch (err) {
      location.replace("/");
    }
  }

  private customServerMessage = (serverCode: ServerCustomCode) => {};

  private async setTranslationsInMessages() {
    const translationObject = await this.i18nService.getTranslations();
    this.messages.next(translationObject);
    localStorage.setItem("messages", JSON.stringify(translationObject));
  }

  private getData(response: Response, url: string) {
    if (response && response.url.indexOf(url) >= 0 && response.json()) {
      return response.json();
    } else {
      this.authenticationService.logout();
      location.reload();
      return;
    }
  }

  private headers() {
    let headers = new Headers({ "Content-Type": "application/json" });
    return new RequestOptions({ headers: headers });
  }

  private multiFilesHeaders() {
    let mHeaders = new Headers({
      "Content-Type": "multipart/form-data",
      Accept: "application/json",
    });
    return new RequestOptions({ headers: mHeaders });
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/data.service.ts